import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, ClipboardCheck, Clock, DollarSign, CheckCircle, AlertTriangle, Lock, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Permit { name: string; cost: string; time: string }
interface BudgetItem { item: string; cost: string }
interface TimelineItem { week: string; task: string }

interface Showcase {
  business: string; emoji: string; city: string;
  permits: Permit[]; budgetItems: BudgetItem[]; timeline: TimelineItem[];
  warning: string; totalCost: string; breakEven: string; topLocation: string; avgRent: string;
}

const showcases: Showcase[] = [
  {
    business: "Restaurant", emoji: "🍽️", city: "London, UK",
    permits: [
      { name: "Food Business Registration", cost: "Free", time: "28 days before opening" },
      { name: "Premises Licence (alcohol)", cost: "£300–£1,905", time: "4–8 weeks" },
      { name: "Food Hygiene Rating (Level 2)", cost: "£25/person", time: "1 day" },
      { name: "Gas Safety Certificate", cost: "£150", time: "1 week" },
      { name: "Planning Permission (A3 Use)", cost: "£462", time: "8–12 weeks" },
    ],
    budgetItems: [
      { item: "Lease premium + deposit (Zone 2)", cost: "£85,000" },
      { item: "Commercial kitchen fit-out", cost: "£120,000" },
      { item: "Interior design & furniture", cost: "£75,000" },
      { item: "Equipment (ovens, fridges, extraction)", cost: "£45,000" },
      { item: "Working capital (3 months)", cost: "£60,000" },
      { item: "Staff recruitment & training", cost: "£18,000" },
    ],
    timeline: [
      { week: "Week 1–2", task: "Register Ltd company, secure commercial lease" },
      { week: "Week 3–4", task: "Submit premises licence & planning applications" },
      { week: "Week 5–12", task: "Full fit-out: kitchen, dining, HVAC, extraction" },
      { week: "Week 13–14", task: "Equipment install, menu development" },
      { week: "Week 15–16", task: "EHO inspection, staff training, soft launch" },
      { week: "Week 17–18", task: "Grand opening" },
    ],
    warning: "London extraction systems require planning permission and can add £15,000–£30,000 to buildout costs.",
    totalCost: "£350,000 – £650,000", breakEven: "12–18 months",
    topLocation: "Shoreditch / Soho", avgRent: "£8,500/mo",
  },
  {
    business: "Coffee Shop", emoji: "☕", city: "Dubai, UAE",
    permits: [
      { name: "DED Trade License", cost: "AED 15,000", time: "5–7 days" },
      { name: "Dubai Municipality Food Permit", cost: "AED 5,000", time: "2–3 weeks" },
      { name: "HACCP Certification", cost: "AED 3,000", time: "1–2 weeks" },
      { name: "Civil Defence Approval", cost: "AED 2,000", time: "2 weeks" },
      { name: "Signage Permit (RTA)", cost: "AED 1,500", time: "1 week" },
    ],
    budgetItems: [
      { item: "Lease deposit + EJARI (Downtown)", cost: "AED 120,000" },
      { item: "Specialty espresso machine", cost: "AED 55,000" },
      { item: "Interior fit-out & branding", cost: "AED 180,000" },
      { item: "Grinders, brewers, cold brew systems", cost: "AED 25,000" },
      { item: "Working capital (3 months)", cost: "AED 90,000" },
      { item: "Staff visas & training", cost: "AED 40,000" },
    ],
    timeline: [
      { week: "Week 1", task: "Register LLC/Free Zone, open corporate bank account" },
      { week: "Week 2–3", task: "Secure lease, submit DED & municipality applications" },
      { week: "Week 4–8", task: "Interior fit-out: counters, plumbing, electrical" },
      { week: "Week 9–10", task: "Equipment install, staff visa processing" },
      { week: "Week 11", task: "Municipality inspection, barista training" },
      { week: "Week 12", task: "Soft opening → grand opening" },
    ],
    warning: "Dubai requires staff health cards (AED 320/person) and all employees need sponsored visas — budget AED 7,000–10,000 per hire.",
    totalCost: "AED 550,000 – AED 1,100,000", breakEven: "8–14 months",
    topLocation: "JBR / Downtown Dubai", avgRent: "AED 25,000/mo",
  },
  {
    business: "Bar & Lounge", emoji: "🍸", city: "New York, NY",
    permits: [
      { name: "NYC Business Certificate", cost: "$105", time: "1 week" },
      { name: "SLA Liquor License (On-Premise)", cost: "$4,352", time: "3–6 months" },
      { name: "Certificate of Occupancy", cost: "$100", time: "2–4 weeks" },
      { name: "FDNY Fire Suppression Permit", cost: "$280", time: "2–3 weeks" },
      { name: "Cabaret License (if live music)", cost: "$150", time: "30 days" },
    ],
    budgetItems: [
      { item: "Lease deposit + broker fee", cost: "$85,000" },
      { item: "Bar buildout (custom counters, plumbing)", cost: "$180,000" },
      { item: "Sound system + lighting rig", cost: "$35,000" },
      { item: "Initial liquor inventory", cost: "$22,000" },
      { item: "POS, security cameras, ID scanner", cost: "$12,000" },
      { item: "Working capital (3 months)", cost: "$75,000" },
    ],
    timeline: [
      { week: "Day 1", task: "Apply for SLA liquor license immediately" },
      { week: "Week 1–2", task: "Form LLC, secure lease, hire architect" },
      { week: "Week 3–10", task: "Full buildout: bar, kitchen, bathrooms, HVAC" },
      { week: "Week 11–14", task: "Hire staff, train, finalize cocktail menu" },
      { week: "Week 15–18", task: "Inspections: fire, health, building dept." },
      { week: "Week 19+", task: "License approval → soft opening" },
    ],
    warning: "NYC liquor licenses take 3–6 months and require a 500-ft rule check near schools/churches.",
    totalCost: "$325,000 – $780,000", breakEven: "14–22 months",
    topLocation: "Lower East Side / Williamsburg", avgRent: "$14,500/mo",
  },
  {
    business: "Bakery", emoji: "🧁", city: "São Paulo, Brazil",
    permits: [
      { name: "CNPJ Registration", cost: "R$ 0", time: "3–5 days" },
      { name: "Alvará de Funcionamento", cost: "R$ 500", time: "2–4 weeks" },
      { name: "ANVISA Sanitary Permit", cost: "R$ 800", time: "2–3 weeks" },
      { name: "Fire Department Certificate (AVCB)", cost: "R$ 1,200", time: "3–6 weeks" },
      { name: "Municipal Signage License", cost: "R$ 300", time: "1–2 weeks" },
    ],
    budgetItems: [
      { item: "Lease deposit (3 months, Jardins)", cost: "R$ 36,000" },
      { item: "Commercial ovens & proofers", cost: "R$ 85,000" },
      { item: "Mixers, display cases, refrigeration", cost: "R$ 55,000" },
      { item: "Interior buildout & counters", cost: "R$ 120,000" },
      { item: "Working capital (3 months)", cost: "R$ 75,000" },
      { item: "Packaging, branding, signage", cost: "R$ 25,000" },
    ],
    timeline: [
      { week: "Week 1", task: "Register MEI/ME, open business bank account" },
      { week: "Week 2–3", task: "Sign lease, submit alvará & ANVISA applications" },
      { week: "Week 4–7", task: "Buildout: electrical upgrades, vent hood, ovens" },
      { week: "Week 8–9", task: "Equipment setup, recipe testing at scale" },
      { week: "Week 10", task: "ANVISA inspection, hire staff" },
      { week: "Week 11–12", task: "Soft opening → grand opening" },
    ],
    warning: "ANVISA inspections in São Paulo can be strict — ensure your kitchen layout meets NR-12 safety standards before scheduling.",
    totalCost: "R$ 400,000 – R$ 900,000", breakEven: "8–14 months",
    topLocation: "Vila Madalena / Pinheiros", avgRent: "R$ 12,000/mo",
  },
];

const ITEM_REVEAL_MS = 250;

export default function LandingPreview({ onSignUp }: { onSignUp: () => void }) {
  const [idx, setIdx] = useState(0);
  const [tab, setTab] = useState<"permits" | "budget" | "timeline">("permits");
  const [revealCount, setRevealCount] = useState(0);
  const [paused, setPaused] = useState(false);
  const current = showcases[idx];

  const items = tab === "permits" ? current.permits : tab === "budget" ? current.budgetItems : current.timeline;

  useEffect(() => {
    setRevealCount(0);
    const iv = setInterval(() => {
      setRevealCount(p => { if (p >= items.length) { clearInterval(iv); return p; } return p + 1; });
    }, ITEM_REVEAL_MS);
    return () => clearInterval(iv);
  }, [idx, tab, items.length]);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => { setIdx(p => (p + 1) % showcases.length); setTab("permits"); }, 8000);
    return () => clearInterval(t);
  }, [paused]);

  const pick = useCallback((i: number) => { setIdx(i); setTab("permits"); setPaused(true); setTimeout(() => setPaused(false), 15000); }, []);
  const pickTab = useCallback((t: "permits" | "budget" | "timeline") => { setTab(t); setPaused(true); setTimeout(() => setPaused(false), 15000); }, []);

  return (
    <div className="bg-card rounded-2xl border border-border shadow-elevated overflow-hidden"
      onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}
    >
      {/* Toolbar — mimics a SaaS app chrome */}
      <div className="px-5 py-3 border-b border-border bg-muted/30 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-destructive/40" />
            <div className="w-2.5 h-2.5 rounded-full bg-accent/50" />
            <div className="w-2.5 h-2.5 rounded-full bg-primary/40" />
          </div>
          <div className="hidden sm:flex items-center gap-1.5 text-[11px] text-muted-foreground/60 bg-background/60 border border-border/60 rounded-md px-2.5 py-1">
            <Lock className="w-2.5 h-2.5" /> app.oplif.com/roadmap
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5 text-primary/60" />
          <span className="text-[11px] font-medium text-muted-foreground/70 tracking-wide uppercase">Live Preview</span>
        </div>
      </div>

      <div className="p-5 sm:p-8">
        {/* Business pills */}
        <div className="flex flex-wrap gap-2 mb-6">
          {showcases.map((s, i) => (
            <button key={s.business} onClick={() => pick(i)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 ${
                i === idx ? "bg-primary/10 text-primary border border-primary/25 shadow-sm" : "bg-muted/50 text-muted-foreground border border-transparent hover:text-foreground hover:bg-muted"
              }`}
            >{s.emoji} {s.business}</button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div key={idx} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.3, ease: [0.21, 0.47, 0.32, 0.98] }}>
            {/* Title + stats */}
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-5">
              <div>
                <h3 className="font-heading font-medium text-foreground text-lg sm:text-xl">{current.emoji} {current.business} in {current.city}</h3>
                <p className="text-xs text-muted-foreground/70 mt-1">AI-generated roadmap</p>
              </div>
              <div className="flex gap-5">
                <div className="text-right">
                  <div className="text-[11px] text-muted-foreground/60 uppercase tracking-wider">Startup cost</div>
                  <div className="font-heading font-medium text-sm text-foreground mt-0.5">{current.totalCost}</div>
                </div>
                <div className="text-right">
                  <div className="text-[11px] text-muted-foreground/60 uppercase tracking-wider">Break-even</div>
                  <div className="font-heading font-medium text-sm text-primary mt-0.5">{current.breakEven}</div>
                </div>
              </div>
            </div>

            {/* Warning */}
            <div className="flex items-start gap-2.5 bg-accent/8 border border-accent/15 rounded-lg p-3 mb-5">
              <AlertTriangle className="w-4 h-4 text-accent shrink-0 mt-0.5" />
              <p className="text-[13px] text-foreground/90 leading-relaxed"><span className="font-medium">⚠ Warning:</span> {current.warning}</p>
            </div>

            {/* Location */}
            <div className="flex items-center gap-4 bg-muted/30 rounded-lg border border-border/60 p-3 mb-5">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-primary" />
                <div><div className="text-[11px] text-muted-foreground/60 uppercase tracking-wider">Best neighborhood</div><div className="text-sm font-medium text-foreground mt-0.5">{current.topLocation}</div></div>
              </div>
              <div className="w-px h-8 bg-border/60" />
              <div><div className="text-[11px] text-muted-foreground/60 uppercase tracking-wider">Avg. rent</div><div className="text-sm font-medium text-foreground mt-0.5">{current.avgRent}</div></div>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 bg-muted/40 rounded-lg p-1 mb-5">
              {([
                { key: "permits" as const, label: "Permits & Licenses", short: "Permits", icon: ClipboardCheck },
                { key: "budget" as const, label: "Startup Costs", short: "Costs", icon: DollarSign },
                { key: "timeline" as const, label: "Opening Timeline", short: "Timeline", icon: Clock },
              ]).map(t => (
                <button key={t.key} onClick={() => pickTab(t.key)}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-md text-xs sm:text-[13px] font-medium transition-all duration-200 ${
                    tab === t.key ? "bg-card text-foreground shadow-sm border border-border/60" : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <t.icon className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">{t.label}</span>
                  <span className="sm:hidden">{t.short}</span>
                </button>
              ))}
            </div>

            {/* Tab content */}
            <div className="space-y-1.5">
              {tab === "permits" && (current.permits).map((p, i) => (
                <motion.div key={p.name} initial={{ opacity: 0, x: -12 }} animate={i < revealCount ? { opacity: 1, x: 0 } : { opacity: 0, x: -12 }} transition={{ duration: 0.25, ease: "easeOut" }}
                  className="flex items-center justify-between p-3 bg-background rounded-lg border border-border/60 hover:border-border transition-colors"
                >
                  <div className="flex items-center gap-2.5"><CheckCircle className="w-4 h-4 text-primary shrink-0" /><span className="text-[13px] font-medium text-foreground">{p.name}</span></div>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground shrink-0"><span className="font-medium text-foreground/80">{p.cost}</span><span className="hidden sm:inline text-border">·</span><span className="hidden sm:inline">{p.time}</span></div>
                </motion.div>
              ))}
              {tab === "budget" && (current.budgetItems).map((b, i) => (
                <motion.div key={b.item} initial={{ opacity: 0, x: -12 }} animate={i < revealCount ? { opacity: 1, x: 0 } : { opacity: 0, x: -12 }} transition={{ duration: 0.25, ease: "easeOut" }}
                  className="flex items-center justify-between p-3 bg-background rounded-lg border border-border/60 hover:border-border transition-colors"
                >
                  <span className="text-[13px] font-medium text-foreground">{b.item}</span>
                  <span className="text-[13px] text-foreground/80 font-medium shrink-0">{b.cost}</span>
                </motion.div>
              ))}
              {tab === "timeline" && (current.timeline).map((t, i) => (
                <motion.div key={t.week} initial={{ opacity: 0, x: -12 }} animate={i < revealCount ? { opacity: 1, x: 0 } : { opacity: 0, x: -12 }} transition={{ duration: 0.25, ease: "easeOut" }}
                  className="flex items-start gap-3 p-3 bg-background rounded-lg border border-border/60 hover:border-border transition-colors"
                >
                  <span className="text-[11px] font-medium text-primary bg-primary/8 px-2 py-1 rounded shrink-0 whitespace-nowrap">{t.week}</span>
                  <span className="text-[13px] text-foreground leading-relaxed">{t.task}</span>
                </motion.div>
              ))}
            </div>

            {/* Progress dots */}
            <div className="flex justify-center gap-1.5 mt-5">
              {showcases.map((_, i) => (
                <button key={i} onClick={() => pick(i)}
                  type="button"
                  aria-label={`Show slide ${i + 1}`}
                  aria-current={i === idx ? "true" : undefined}
                  className={`h-1.5 rounded-full transition-all duration-300 ${i === idx ? "bg-primary w-6" : "bg-muted-foreground/20 w-1.5 hover:bg-muted-foreground/40"}`}
                />
              ))}
            </div>

            {/* CTA */}
            <div className="mt-6 text-center">
              <Button size="lg" className="gap-2 shadow-md hover:shadow-lg hover:scale-[1.02] transition-all duration-200" onClick={onSignUp}>
                Get your full roadmap free <Lock className="w-3.5 h-3.5" />
              </Button>
              <p className="text-[11px] text-muted-foreground/60 mt-2">Sign up to unlock your personalized plan</p>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
